import React, { useState, useEffect } from 'react';
import FileUploader from 'devextreme-react/file-uploader';
import { GenericPopup } from './GenericPopup';
import { ButtonTheme } from './Button';
import '../../scss/controls/_fileselectpopup.scss';

interface FileSelectPopupProps {
    id: string;
    options: {
        ownerId?: string;
        isOpen: boolean;
        fileTypeName?: string;
        maxFiles: number;
    };
    onSubmit: (ownerId: any, currentFiles: any) => void;
    onCancel: () => void;
}

export const FileSelectPopup = ({
    id,
    options = {
        ownerId: undefined,
        isOpen: false,
        fileTypeName: undefined,
        maxFiles: 1,
    },
    onSubmit,
    onCancel,
}: FileSelectPopupProps) => {
    const maxFileSize = 1024 * 1024 * 35;
    const [currentFiles, set_currentFiles] = useState<File[]>([]);
    const [canContinue, set_canContinue] = useState(false);

    useEffect(() => {
        if (!options) {
            options = {
                ownerId: undefined,
                isOpen: false,
                fileTypeName: undefined,
                maxFiles: 1,
            };
        }
        if (options.maxFiles < 1) {
            options.maxFiles = 1;
        }
    }, [options]);

    const _onSubmit = () => {
        onSubmit && onSubmit(options.ownerId, currentFiles);
    };

    const _onCancel = () => onCancel && onCancel();

    const onFilesChanged = (fileArray: File[]) => {
        const files = fileArray.filter((file: File) => file.size <= maxFileSize).slice(0, options.maxFiles);
        if (files && files.length > 0) {
            set_canContinue(true);
        } else {
            set_canContinue(false);
        }
        set_currentFiles(files);
    };

    const actionButtons = [
        {
            id: 'cancelUploadButton',
            theme: ButtonTheme.LinkBlue,
            disabled: false,
            children: 'Cancel',
            width: '120px',
            handleClick: _onCancel,
        },
        {
            id: 'continueUploadButton',
            theme: ButtonTheme.Primary,
            disabled: !canContinue,
            children: 'Continue',
            width: '120px',
            handleClick: _onSubmit,
        },
    ];

    if (!(options.isOpen ?? false)) {
        return '';
    }

    return (
        <>
            <GenericPopup
                id={id}
                isOpen={true}
                actionButtons={actionButtons}
                handleClose={_onCancel}
                aria-labelledby='alert-dialog-title'
                hideOnOutsideClick={true}
                showCloseButton={true}
                showTitle={true}
                title='File Upload'
                width='500px'
                height='400px'
                maxWidth='90%'
                maxHeight='70%'
                buttonAlignment='right'
                wrapTitle={true}
            >
                <div className='fileSelectPopup'>
                    {options.fileTypeName && (
                        <div className='fileSelectPopup-typename'>
                            Type: <span style={{ fontWeight: 'bold' }}>{options.fileTypeName}</span>
                        </div>
                    )}
                    <div className='fileSelectPopup-directions'>
                        <ul>
                            {options.maxFiles > 1 && (
                                <li>
                                    You may select up to <b>{options.maxFiles}</b> files. Any others beyond that will NOT be
                                    uploaded.
                                </li>
                            )}
                            {options.maxFiles === 1 && <li>Please select or drop only 1 file to upload</li>}
                            <li>Only pdf, png, jpg and txt file types are allowed</li>
                            <li>Maximum file size is 35 MB/document</li>
                        </ul>
                    </div>
                    <div className='fileSelectPopup-picker'>
                        <div
                            id='drop_zone'
                            className='fileSelectPopup-picker-dropzone'
                        >
                            <FileUploader
                                selectButtonText='Select Files'
                                labelText='OR drag onto this panel...'
                                uploadMode='useForm'
                                maxFileSize={maxFileSize}
                                invalidFileExtensionMessage='Invalid File Type. File will be ignored'
                                invalidMaxFileSizeMessage='File too large. File will be ignored'
                                multiple={options.maxFiles > 1}
                                allowedFileExtensions={['.pdf', '.png', '.jpg', '.txt']}
                                allowCanceling={true}
                                onValueChange={onFilesChanged}
                            />
                        </div>
                    </div>
                </div>
            </GenericPopup>
        </>
    );
};
